<script lang="ts">
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiAlertCircleOutline } from "@mdi/js"
  import type { BackgroundJobProgressView } from "../../_logic/job-progress-contract"

  const { view }: { view: BackgroundJobProgressView } = $props()
  const percent = $derived(Math.min(100, Math.max(0, Math.round(view.percent))))
  const failed = $derived(view.state === "failed")
  const succeeded = $derived(view.state === "succeeded")
</script>

{#if failed}
  <div class="process-error-card" role="alert">
    <div class="error-icon"><Icon path={mdiAlertCircleOutline} /></div>
    <div class="process-error-copy">
      <p class="eyebrow eyebrow--error">{view.eyebrow}</p>
      <h3>{view.errorMessage || view.headline}</h3>
      <dl class="process-meta process-meta--error">
        {#each view.details as detail (detail.label)}
          <div>
            <dt>{detail.label}</dt>
            <dd>{detail.value}</dd>
          </div>
        {/each}
      </dl>
    </div>
  </div>
{:else}
  <section
    class:ready={succeeded}
    class="process-card"
    aria-label={view.ariaLabel}
    aria-live="polite"
  >
    <div class="process-copy">
      <div class:ready={succeeded} class="process-spinner" aria-hidden="true"><span></span></div>
      <div>
        <p class="eyebrow">{view.eyebrow}</p>
        <h3>{view.headline}</h3>
      </div>
      <strong class="percent">{percent}%</strong>
    </div>
    <div
      class="progress-track"
      role="progressbar"
      aria-label={view.ariaLabel}
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={percent}
      aria-valuetext={view.progressText}
    >
      <div class="progress-fill" style={`width: ${percent}%`}></div>
    </div>
    <dl class="process-meta">
      {#each view.details as detail (detail.label)}
        <div>
          <dt>{detail.label}</dt>
          <dd>{detail.value}</dd>
        </div>
      {/each}
    </dl>
  </section>
{/if}

<style lang="less">
  .process-card {
    position: relative;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--success-100) 38%, transparent);
    background:
      linear-gradient(
        90deg,
        color-mix(in srgb, var(--success-100) 16%, transparent),
        transparent 58%
      ),
      color-mix(in srgb, var(--text-100) 8%, transparent);
    padding: 1rem;
    color: var(--text-100);
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--text-100) 12%, transparent);
  }

  .process-card.ready {
    border-color: color-mix(in srgb, var(--success-100) 70%, transparent);
  }

  .process-copy {
    display: flex;
    align-items: center;
    gap: 0.9rem;
  }

  .process-copy h3,
  .process-copy p,
  .process-meta,
  .process-meta dt,
  .process-meta dd,
  .process-error-card h3,
  .process-error-card p {
    margin: 0;
  }

  .process-copy h3,
  .process-error-card h3 {
    font-size: 1.15rem;
    line-height: 1.25;
  }

  .eyebrow {
    font-size: 0.72rem;
    text-transform: uppercase;
    font-weight: 900;
    color: var(--success-100);
  }

  .eyebrow--error {
    color: var(--error-100);
  }

  .percent {
    margin-left: auto;
    font-size: 1rem;
    color: var(--text-100);
  }

  .process-spinner {
    width: 2.25rem;
    height: 2.25rem;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
  }

  .process-spinner span {
    width: 1.55rem;
    height: 1.55rem;
    border: 0.22rem solid color-mix(in srgb, var(--text-100) 28%, transparent);
    border-top-color: var(--success-100);
    border-right-color: var(--success-100);
    border-radius: 999px;
    box-shadow: 0 0 0.9rem color-mix(in srgb, var(--success-100) 36%, transparent);
    animation: process-spinner-rotate 0.75s linear infinite;
  }

  .process-spinner.ready span {
    animation: none;
    border-color: var(--success-100);
  }

  .progress-track {
    position: relative;
    overflow: hidden;
    height: 0.65rem;
    margin: 1rem 0 0.5rem;
    background: color-mix(in srgb, var(--text-100) 24%, transparent);
    border: 1px solid color-mix(in srgb, var(--text-100) 20%, transparent);
  }

  .progress-fill {
    position: relative;
    display: block;
    height: 100%;
    overflow: hidden;
    background: var(--success-100);
    box-shadow: 0 0 0.85rem color-mix(in srgb, var(--success-100) 35%, transparent);
    transition: width 0.2s ease;
  }

  .progress-fill::after {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      115deg,
      color-mix(in srgb, var(--text-100) 34%, transparent) 0,
      color-mix(in srgb, var(--text-100) 34%, transparent) 0.42rem,
      transparent 0.42rem,
      transparent 0.9rem
    );
    animation: progress-stripes 0.75s linear infinite;
  }

  .process-card.ready .progress-fill::after {
    animation: none;
    opacity: 0;
  }

  .process-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 2.25rem;
    color: color-mix(in srgb, var(--text-100) 82%, transparent);
  }

  .process-meta dt {
    color: color-mix(in srgb, var(--text-100) 62%, transparent);
    font-size: 0.68rem;
    font-weight: 900;
    text-transform: uppercase;
  }

  .process-meta dd {
    margin-top: 0.12rem;
    font-weight: 700;
  }

  .process-error-card {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    border: 1px solid color-mix(in srgb, var(--error-100) 62%, transparent);
    background: color-mix(in srgb, var(--error-100) 10%, transparent);
    padding: 1rem;
    color: var(--text-100);
  }

  .error-icon {
    width: 2rem;
    height: 2rem;
    display: grid;
    place-items: center;
    color: var(--error-100);
    flex: 0 0 auto;
  }

  .process-error-card h3 {
    color: var(--error-100);
    font-weight: 800;
  }

  .process-error-copy {
    min-width: 0;
  }

  .process-meta--error {
    margin-top: 0.8rem;
  }

  .process-meta--error dd {
    overflow-wrap: anywhere;
  }

  @keyframes process-spinner-rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes progress-stripes {
    100% {
      transform: translateX(0.9rem);
    }
  }
</style>
